import { Action } from "redux";

import {
    AllowedMenuItemCodes,
    IconCode,
    IFormProfile,
    IMenuData,
    IMenuItemData,
    INotificationItem,
    IOpenedFormHistoryInfo,
    NotificationGroupId,
} from "@multiweb/types/contracts";
import { MutableTypedObject, SerializableKeyEvent, SerializableMouseEvent, TypedObject } from "@multiweb/types/react";
import {
    ADD_PROFILE_TO_RELATIONSHIPS,
    CLEAR_LAST_SUBMENU,
    CLOSE_SUBMENUS,
    CONCAT_NOTIFICATION_ITEMS,
    CONCAT_PANEL_ITEMS,
    INCREASE_NOTIFICATIONS_PAGE_COUNT,
    LOAD_NEXT_NOTIFICATION_ITEMS_PAGE,
    MENU_ITEM_ACTIVATED,
    MENU_ITEM_DEFAULT_ACTION_ALLOWED,
    RELOAD_MENU,
    REMOVE_PROFILE_FORM_RELATIONSHIPS,
    REMOVE_PROFILE_FROM_FORMS,
    RESET_NOTIFICATION_ITEMS,
    RESET_NOTIFICATIONS_PAGE_COUNT,
    SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ,
    SET_ACTIVE_SECTION,
    SET_ACTIVE_SUBMENU,
    SET_ALLOWED_MENU_ITEM_CODES,
    SET_FORM_ITEM_ACTION_RESTRICTIONS,
    SET_MENU_FLOAT_TRANSITION_ACTIVE,
    SET_MENU_ITEMS,
    SET_MENU_PANEL_CAPTIONS_VISIBILITY,
    SET_MENU_VISIBILITY,
    SET_NOTIFICATION_GROUPS,
    SET_NOTIFICATION_GROUPS_PANEL_ITEMS,
    SET_PROFILE_ACTION_RESTRICTIONS,
    SET_VISUAL_AUTOMAT_STATE,
    SHOW_GROUP_NOTIFICATIONS,
    TOGGLE_SUBMENU,
    VISUAL_AUTOMAT_PROCESSED,
} from "./constants";
import { IFavoriteItemsState } from "./favoriteItems/types";
import { IMenuNavigationState, NavigationSection } from "./navigation/types";
import { IMenuOpenedItemsState } from "./openedItems/types";
import { ISearchState } from "./search/types";

/**
 * Definice stavu pro hlavní menu
 */
export interface IMenuState {
    /** Část stavu menu obsahující položky menu */
    readonly items: IMenuItems;
    /** Viditelnost menu */
    readonly menuVisibility: MenuVisibility;
    /** Indikátor určující, zda mají být zobrazeny popisky položek v menu panelu. */
    readonly menuPanelCaptionsVisible: boolean;
    /** Příznak, zda je viditelná překryvná vrstva formulářové oblasti */
    readonly formOverlayVisible: boolean;
    /** Obsahuje klíč aktivní sekce v levém panelu menu */
    readonly activeSectionId: SectionId;
    /** Obsahuje id nejzanořenější rozbalené submenu položky */
    readonly activeSubmenuId: ActiveSubmenuId;
    /** Obsahuje statické položky z levého panelu menu */
    readonly panelItems: MenuPanelItems;
    /**
     * Příznak, zda je aktivní float přechod menu. Tento příznak je nutné uložit pro případ přechodu menu z překrývajícího režimu
     * do skrytého a naopak. V opačném případě by se překrývající menu při skrývání chovalo jako nepřekrývající a podle toho by se animovalo.
     */
    readonly menuFloatTransitionActive: boolean;
    /** Obsahuje data potřebná pro navigaci pomocí klávesnice */
    readonly navigation: IMenuNavigationState;
    /** Obsahuje data o otevřených položkách. */
    readonly openedItems: IMenuOpenedItemsState;
    /** Obsahuje data související s vyhledáváním */
    readonly search: ISearchState;
    /** Obsahuje data související s oblíbenými položkami */
    readonly favoriteItems: IFavoriteItemsState;
}

export type FormItemIdArray = ReadonlyArray<number>;
export type Forms = Readonly<{ [id: number]: IMenuItemData}>;
export type Profiles = Readonly<{ [id: number]: IFormProfile}>;
export type SectionId = string;
export type ActiveSubmenuId = number | null;
export type FavoriteFormIds = FormItemIdArray;

/**
 * Mapuje kód formuláře na položky menu. Jeden formulář může mít v menu více položek.
 */
export type FormCodeMap = TypedObject<readonly number[]>;

/**
 * Modifikovatelná varianta {@link FormCodeMap} pro použití při sestavování finálního objektu.
 */
export type MutableFormCodeMap = MutableTypedObject<number[]>;

/**
 * Část stavu uchovávající informace o notifikacích.
 */
export type Notifications = {
    /** Notifikační položky. */
    readonly items: Readonly<NotificationItems>;
    /** Notifikační skupiny. */
    readonly groups: Readonly<FlatNotificationGroups>;
    /** Počet načtených stránek notifikačních položek. */
    readonly pageCount: Readonly<number>;
};

export type OpenedFormHistoryInfoArray = ReadonlyArray<IOpenedFormHistoryInfo>;

/**
 * Část stavu uchovávající položky pro menu.
 */
export interface IMenuItems {
    /** Aliasy povolených kořenových uzlů hlavního navigačního menu. */
    readonly allowedMenuItemCodes: AllowedMenuItemCodes;
    /** Položky formulářů indexované přes identifikátory. */
    readonly forms: Forms;
    /** Položky formulářových profilů indexované přes identifikátory. */
    readonly profiles: Profiles;
    /** Pole identifikátorů formulářových položek, které se zobrazují v první úrovni formulářového menu. */
    readonly rootFormItemIds: FormItemIdArray;
    /** Pomocné mapování z kódu formuláře na jeho id. Předchází opakovanému vyhledávání pro vazby využívající formCode (profily, oblíbené). */
    readonly formCodeMap: FormCodeMap;
    /** Podstrom notifikací. */
    readonly notifications: Notifications;
    /** Pole objektů s informacemi o formulářích otevřených v minulosti. Nejnovější záznam v historii je na začátku pole. */
    readonly historyFormsInfo: OpenedFormHistoryInfoArray;
    /** Obsahuje informace o omezeních pro položky menu. */
    readonly restrictions: Restrictions;
    /** Obsahuje propojení mezi profily a položky formulářů */
    readonly profileFormRelationships: ProfileFormRelationships;
}

export type Restrictions = {
    /** Omezení pro formulářové položky zakazující zobrazení akčních položek pro otevření nových instancí. */
    readonly formItemHideWindowActions: WindowActionsRestrictions;
    /** Omezení pro profilové položky zakazující zobrazení akčních položek pro otevření nových instancí. */
    readonly profileHideWindowActions: WindowActionsRestrictions;
    /** Omezení pro instance formulářových položky zakazující zobrazení akční položky pro zavření instance. */
    readonly formItemInstanceHideCloseAction: FormItemInstanceCloseRestrictions;
};

/**
 * Objekt mapující id formulářové nebo profilové položky na hodnotu indikující omezení zobrazení akcí pro otevření nové instance.
 * Pokud položka nemá v objektu své id, potom není omezená.
 */
export type WindowActionsRestrictions = Readonly<{[id: number]: boolean}>;

/**
 * Objekt mapující id formulářové položky na hodnotu indikující omezení zobrazení akce pro zavření instance.
 * Pokud položka nemá v objektu své id, potom není omezená.
 */
export type FormItemInstanceCloseRestrictions = Readonly<TypedObject<boolean>>;

/**
 * Typ položky levého panelu menu
 */
export enum MenuPanelItemType {
    static = "static",
    notificationGroup = "notificationGroup",
    form = "form",
}

/**
 * Struktura dat pro položku levého panelu menu.
 */
export interface IMenuPanelItem {
    readonly id: string;
    readonly iconCode: IconCode;
    readonly locKey: string;
    readonly itemId?: string | number;
    readonly highlighted?: boolean;
    readonly type: MenuPanelItemType;
    readonly activationType: MenuItemActivatedType;
    /**
     * Id podmenu, které bylo naposledy otevřené.
     * Slouží k reaktivaci tohoto podmenu spolu s aktivací položky hlavního panelu menu.
     */
    readonly lastSubmenuId?: number;
}

/**
 * Typ pro normalizovaný stav uchovávající položky levého panelu menu.
 */
export type MenuPanelItems = Readonly<TypedObject<IMenuPanelItem>>;

/**
 * Část stavu uchovávající propojení mezi profilem a položkou formuláře.
 */
export type ProfileFormRelationships = TypedObject<ProfileFormRelationship>;

/**
 * Upřesňuje propojení mezi profilem a položkou formuláře. Umožňuje jednoznačnou identifikaci pro profil, i když je vykreslený vícekrát, pod různými položkami formulářů.
 * Profily mají i své samostatné záznamy, kvůli jednotné práci s případy, kdy je profil vykreslený samostatně, bez rodičovského formuláře. To nastává například v sekci
 * vyhledávání.
 */
export type ProfileFormRelationship = {
    /** Id složené z id profilu a položky formuláře, u které je vedený jako potomek, pomocí funkce createItemIdForProfile z navigation sub modulu. */
    readonly id: string;
    readonly formItemId?: number;
    readonly profileId: number;
};

/**
 * Akce pro nastavení položek menu do stavu.
 */
export interface ISetMenuItemsAction extends Action<typeof SET_MENU_ITEMS> {
    readonly payload: {
        readonly menuItems: Readonly<IMenuData>;
    };
}

export class ISetMenuItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_MENU_ITEMS;
    }
}

/** Typ tlačítka ovlivňuje barvu pozadí po najetí myši */
export enum ActionItemHoverType {
    /** Výchozí typ */
    primary = "primary",
    /** Typ destruktvivní akce */
    danger = "danger",
}

/** Notifikační položky jsou ve stavu ukládány v objektu klíčovaném přes jejich identifikátory. */
export type NotificationItems = Readonly<TypedObject<INotificationItem>>;

/** Plochá reprezentace notifikační skupiny. */
export interface IFlatNotificationGroup {
    /** Identifikátor skupiny notifikací */
    readonly id: NotificationGroupId;
    /** Zda je skupina nepřečtená */
    readonly unread: boolean;
    /** Kód formuláře, který se používá pro zobrazení všech notifikací skupiny */
    readonly formCode: string;
    /** Kód ikony pro skupinu */
    readonly iconCode: IconCode;
    /** Lokalizační klíč titulku v hlavičce skupiny notifikací */
    readonly titleLocKey: string;
}

/** Notifikační skupiny jsou ve stavu pro snažší práci ukládány v plochém formátu. */
export type FlatNotificationGroups = {[notificationGroupId: string]: IFlatNotificationGroup};

/**
 * Viditelnost menu
 */
export enum MenuVisibility {
    /* Menu je skryté */
    hidden = "hidden",
    /* Menu je viditelné a nepřekrývající */
    expanded = "expanded",
    /* Menu je viditelné a překrývající */
    floated = "floated",
}

/**
 * Možné stavy automatu viditelnosti překryvné vrstvy, menu a submenu
 * První číslo názvu stavu představuje viditelnost překryvné vrstvy (skrytá, viditelná), další číslo viditelnost menu (skryté, překrývající,
 * nepřekrývající) a poslední číslo viditelnost submenu (skryté, viditelné).
 */
export type VisualAutomatState = "000" | "020" | "121" | "110" | "111";

/**
 * Typy položek, které se nachází v menu. Slouží k identifikaci typu zdrojové položky v obecných akcích vyvolaných interakcí uživatele s těmito položkami.
 * Nemusí zahrnovat všechny typy položek, pokud některé nevyužívají obecné akce.
 */
export enum MenuItemActivatedType {
    profile = "profile",
    form = "form",
    formFolder = "formFolder",
    panelItemForm = "panelItemForm",
    panelItemFormFolder = "panelItemFormFolder",
    panelItemCombinedItemForm = "panelItemCombinedItemForm",
    panelItemCombinedItemFolder = "panelItemCombinedItemFolder",
    settingsButton = "settingsButton",
    toggleThemeButton = "toggleThemeButton",
    logoutButton = "logoutButton",
    notificationGroup = "notificationGroup",
    notificationItem = "notificationItem",
    staticPanelItem = "staticPanelItem",
    expandButton = "expandButton",
    formFavoriteActionItem = "formFavoriteActionItem",
    openedInstanceFavoriteActionItem = "openedInstanceFavoriteActionItem",
    openFormActionItem = "openFormActionItem",
    openProfileActionItem = "openProfileActionItem",
    openFormInstanceActionItem = "openFormInstanceActionItem",
    openFormInTabActionItem = "openFormInTabActionItem",
    openProfileInTabActionItem = "openProfileInTabActionItem",
    openFormInstanceInTabActionItem = "openFormInstanceInTabActionItem",
    closeActionItem = "closeActionItem",
    logoButton = "logoButton",
    openedForm = "openedForm",
}

/**
 * Typy aktivovatelných položek reprezentovány akční položkou pro nastavení oblíbenosti.
 */
export type FavoriteActionItemActivatableTypes = MenuItemActivatedType.formFavoriteActionItem | MenuItemActivatedType.openedInstanceFavoriteActionItem;

/**
 * Typ parametru pro selector isFormItemOrInstanceFavorite.
 */
export type IsFormItemOrInstanceFavoriteSelectorParams = {
    readonly formId: string | number;
    readonly type: FavoriteActionItemActivatableTypes;
    readonly profileId: number | undefined;
};

export interface ISetMenuVisibilityAction extends Action<typeof SET_MENU_VISIBILITY> {
    readonly payload: {
        readonly menuVisibility: MenuVisibility;
    };
}

export class ISetMenuVisibilityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_MENU_VISIBILITY;
    }
}

export interface ISetActiveSubmenuAction extends Action<typeof SET_ACTIVE_SUBMENU> {
    readonly payload: {
        submenuId: number;
        /** Id položky na hlavním panelu, v rámci které má být aktivováno dané podmenu. */
        sectionId: SectionId;
    };
}

export class ISetActiveSubmenuAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_ACTIVE_SUBMENU;
    }
}

export interface ICloseSubmenusAction extends Action<typeof CLOSE_SUBMENUS> {}

export class ICloseSubmenusAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLOSE_SUBMENUS;
    }
}

export interface IClearLastSubmenuAction extends Action<typeof CLEAR_LAST_SUBMENU> {
    readonly payload: {
        sectionId: SectionId;
    };
}

export class IClearLastSubmenuAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_LAST_SUBMENU;
    }
}

export interface ISetActiveSectionAction extends Action<typeof SET_ACTIVE_SECTION> {
    readonly payload: {
        readonly activeSectionId: SectionId;
    };
}

export class ISetActiveSectionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_ACTIVE_SECTION;
    }
}

export interface IToggleSubmenuAction extends Action<typeof TOGGLE_SUBMENU> {
    readonly payload: {
        readonly submenuId: number;
    };
}

export class IToggleSubmenuAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === TOGGLE_SUBMENU;
    }
}

export interface ISetAllowedMenuItemCodesAction extends Action<typeof SET_ALLOWED_MENU_ITEM_CODES> {
    readonly payload: {
        readonly allowedMenuItemCodes: AllowedMenuItemCodes;
    };
}

export class ISetAllowedMenuItemCodesAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_ALLOWED_MENU_ITEM_CODES;
    }
}

export interface IResetNotificationItemsAction extends Action<typeof RESET_NOTIFICATION_ITEMS> {}

export class IResetNotificationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === RESET_NOTIFICATION_ITEMS;
    }
}

export interface IConcatNotificationItemsAction extends Action<typeof CONCAT_NOTIFICATION_ITEMS> {
    readonly payload: {
        readonly notificationItems: NotificationItems;
    };
}

export class IConcatNotificationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CONCAT_NOTIFICATION_ITEMS;
    }
}

export interface ISetNotificationGroupsAction extends Action<typeof SET_NOTIFICATION_GROUPS> {
    readonly payload: {
        readonly notificationGroups: FlatNotificationGroups;
    };
}

export class ISetNotificationGroupsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_NOTIFICATION_GROUPS;
    }
}

export interface IResetNotificationsPageCountAction extends Action<typeof RESET_NOTIFICATIONS_PAGE_COUNT> {}

export class IResetNotificationsPageCountAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === RESET_NOTIFICATIONS_PAGE_COUNT;
    }
}

export interface IIncreaseNotificationsPageCountAction extends Action<typeof INCREASE_NOTIFICATIONS_PAGE_COUNT> {}

export class IIncreaseNotificationsPageCountAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === INCREASE_NOTIFICATIONS_PAGE_COUNT;
    }
}

export interface ILoadNextNotificationItemsPageAction extends Action<typeof LOAD_NEXT_NOTIFICATION_ITEMS_PAGE> {}

export class ILoadNextNotificationItemsPageAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_NEXT_NOTIFICATION_ITEMS_PAGE;
    }
}

export interface IShowGroupNotificationsAction extends Action<typeof SHOW_GROUP_NOTIFICATIONS> {}

export class IShowGroupNotificationsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SHOW_GROUP_NOTIFICATIONS;
    }
}

export interface ISetActiveGroupNotificationsAsReadAction extends Action<typeof SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ> {}

export class ISetActiveGroupNotificationsAsReadAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ;
    }
}

export interface ISetFormItemActionRestrictionsAction extends Action<typeof SET_FORM_ITEM_ACTION_RESTRICTIONS> {
    readonly payload: {
        readonly formItemId: number;
        readonly hideWindowActions: boolean;
    };
}

export class ISetFormItemActionRestrictionsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_FORM_ITEM_ACTION_RESTRICTIONS;
    }
}

export interface ISetProfileActionRestrictionsAction extends Action<typeof SET_PROFILE_ACTION_RESTRICTIONS> {
    readonly payload: {
        readonly profileId: number;
        readonly hideWindowActions: boolean;
    };
}

export class ISetProfileActionRestrictionsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_PROFILE_ACTION_RESTRICTIONS;
    }
}

export interface IConcatPanelItemsAction extends Action<typeof CONCAT_PANEL_ITEMS> {
    readonly payload: {
        readonly panelItems: MenuPanelItems;
    };
}

export class IConcatPanelItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CONCAT_PANEL_ITEMS;
    }
}

export interface ISetNotificationGroupsPanelItemsAction extends Action<typeof SET_NOTIFICATION_GROUPS_PANEL_ITEMS> {
    readonly payload: {
        readonly notificationGroupsPanelItems: MenuPanelItems;
    };
}

export class ISetNotificationGroupsPanelItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_NOTIFICATION_GROUPS_PANEL_ITEMS;
    }
}

export interface ISetMenuPanelCaptionsVisibilityAction extends Action<typeof SET_MENU_PANEL_CAPTIONS_VISIBILITY> {
    readonly payload: {
        readonly visible: boolean;
    };
}

export class ISetMenuPanelCaptionsVisibilityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.type === SET_MENU_PANEL_CAPTIONS_VISIBILITY;
    }
}

type MenuItemActivatedPayload = {
    readonly itemId: string | number;
    readonly profileId?: number;
    readonly itemType: MenuItemActivatedType;
    readonly section: NavigationSection;
    readonly mouseEvent?: SerializableMouseEvent;
    readonly keyEvent?: SerializableKeyEvent;
    readonly preventFormDefault?: boolean;
};

export interface IMenuItemActivatedAction extends Action<typeof MENU_ITEM_ACTIVATED> {
    readonly payload: MenuItemActivatedPayload;
}

export class IMenuItemActivatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === MENU_ITEM_ACTIVATED;
    }
}

export interface IMenuItemDefaultActionAllowedAction extends Action<typeof MENU_ITEM_DEFAULT_ACTION_ALLOWED> {
    readonly payload: MenuItemActivatedPayload;
}

export class IMenuItemDefaultActionAllowedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === MENU_ITEM_DEFAULT_ACTION_ALLOWED;
    }
}

export interface IVisualAutomatProcessedAction extends Action<typeof VISUAL_AUTOMAT_PROCESSED> {}

export class IVisualAutomatProcessedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === VISUAL_AUTOMAT_PROCESSED;
    }
}

export interface ISetVisualAutomatStateAction extends Action<typeof SET_VISUAL_AUTOMAT_STATE> {
    readonly payload: {
        readonly state: VisualAutomatState;
    };
}

export class ISetVisualAutomatStateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_VISUAL_AUTOMAT_STATE;
    }
}

export interface ISetMenuFloatTransitionActive extends Action<typeof SET_MENU_FLOAT_TRANSITION_ACTIVE> {
    readonly payload: {
        readonly menuFloatTransitionActive: boolean;
    };
}

export class ISetMenuFloatTransitionActive {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_MENU_FLOAT_TRANSITION_ACTIVE;
    }
}

export interface IRemoveProfileFromFormsAction extends Action<typeof REMOVE_PROFILE_FROM_FORMS> {
    readonly payload: {
        readonly profileId: number;
        readonly formItemIds: readonly number[];
    };
}

export class IRemoveProfileFromFormsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_PROFILE_FROM_FORMS;
    }
}

export interface IRemoveProfileFormRelationshipsAction extends Action<typeof REMOVE_PROFILE_FORM_RELATIONSHIPS> {
    readonly payload: {
        readonly ids: readonly string[];
    };
}

export class IRemoveProfileFormRelationshipsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_PROFILE_FORM_RELATIONSHIPS;
    }
}

export interface IAddProfileToRelationshipsAction extends Action<typeof ADD_PROFILE_TO_RELATIONSHIPS> {
    readonly payload: {
        readonly profileId: number;
    };
}

export class IAddProfileToRelationshipsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === ADD_PROFILE_TO_RELATIONSHIPS;
    }
}

export interface IReloadMenuAction extends Action<typeof RELOAD_MENU> {
}

export class IReloadMenuAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === RELOAD_MENU;
    }
}
